import React, { forwardRef, useCallback, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandCheckboxItem,
} from '../command'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Button2 } from '../button'
import { cn } from '../../../lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip'
import { useTranslation } from 'react-i18next'

export type MultiSelectProps = {
  options: { value: string; label: string }[]
  placeholder?: string
  values: string[]
  onValuesChange: (values: string[]) => void
  className?: string
  disabled?: boolean
  emptyMessage?: string
  searchPlaceholder?: string
  numberOfRequiredItems?: number
}

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      placeholder = 'Select options',
      values = [],
      onValuesChange,
      className,
      disabled = false,
      emptyMessage = 'No options found.',
      searchPlaceholder = 'Search options...',
      numberOfRequiredItems = 0,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const { t } = useTranslation()

    const handleSelect = useCallback(
      (value: string) => {
        let newValues: string[]
        if (values.includes(value)) {
          newValues = values.filter((v) => v !== value)
        } else {
          newValues = [...values, value]
        }
        onValuesChange(newValues)
      },
      [values, onValuesChange],
    )

    return (
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button2
            ref={ref}
            variant='outlined'
            role='combobox'
            aria-expanded={open}
            className={cn(
              'flex h-9 w-full items-center justify-between rounded-lg',
              'border border-grey-s2 bg-white px-0 py-2.5 text-xs',
              'hover:shadow-text-input',
              'focus:outline-none focus-visible:outline-none focus-visible:shadow-text-input',
              'disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
              'transition-colors transition-shadow hover:bg-white px-5',
              className,
            )}
            onClick={() => !disabled && setOpen(!open)}
            disabled={disabled}
          >
            {values.length > 0 ? (
              <div className='text-grey-s6 truncate text-left'>{values.join(', ')}</div>
            ) : (
              <div className='text-grey-s5'>{placeholder}</div>
            )}
          </Button2>
        </PopoverTrigger>
        <PopoverContent
          className='w-[var(--radix-popover-trigger-width)] p-2'
          align='start'
          hideClose
        >
          <Command>
            {options.length > 3 && (
              <CommandInput
                placeholder={searchPlaceholder}
                inputSize={'default'}
                className='p-1 mb-1'
              />
            )}
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup className='text-sm'>
                {options.map((option, i) => {
                  const isSelected = values.includes(option.value)
                  const isDisabled = isSelected && values.length <= numberOfRequiredItems
                  return !isDisabled ? (
                    <CommandCheckboxItem
                      key={option.value}
                      value={option.value}
                      selected={isSelected}
                      onSelect={() => handleSelect(option.value)}
                      className={cn('mt-0')}
                    >
                      <span>{option.label}</span>
                    </CommandCheckboxItem>
                  ) : (
                    // If the item is disabled, show a tooltip explaining why it cannot be deselected
                    <CommandCheckboxItem
                      key={option.value}
                      value={option.value}
                      selected={isSelected}
                      onSelect={() => handleSelect(option.value)}
                      className={cn('mt-0')}
                      disabled={isDisabled}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>{option.label}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {numberOfRequiredItems === 1
                              ? t('multiSelect.tooltip.atLeastItems_one')
                              : t('multiSelect.tooltip.atLeastItems_other', {
                                  n: numberOfRequiredItems,
                                })}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </CommandCheckboxItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

MultiSelect.displayName = 'MultiSelect'
